// API 응답 타입
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

// 사용자 등록 요청
interface RegisterRequest {
  email: string;
  name?: string;
}

// 폼 생성 요청
interface CreateFormRequest {
  name: string;
  target_email: string;
  description?: string;
  prompt: string;
}

export interface FormData {
  form_id: string;
  name: string;
  target_email?: string | null;
  allowed_domain?: string | null;
}

export interface StatsData {
  totalForms: number;
  connectedForms: number;
  unconnectedForms: number;
  totalSubmissions: number;
}

export interface ActivityData {
  type: string;
  message: string;
  created_at: string;
}

export interface FormInfo {
  form_id: string;
  name: string;
  target_email?: string;
  allowed_domain?: string;
  created_at: string;
  submission_count: number;
}

export interface SubmissionInfo {
  id: string;
  form_id: string;
  form_name: string;
  form_data: string;
  client_ip?: string;
  created_at: string;
}

export interface EmailLogInfo {
  id: string;
  form_id: string;
  form_name: string;
  to_email: string;
  subject: string;
  status: 'sent' | 'failed' | 'pending';
  error_message?: string;
  created_at: string;
}

// API 클라이언트 클래스
export class ApiClient {
  private baseUrl: string;
  private apiKey: string | null = null;
  private adminApiKey: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/$/, ''); // 끝의 슬래시 제거
  }

  // API 키 설정
  setApiKey(apiKey: string) {
    this.apiKey = apiKey;
  }

  // API 키 제거
  clearApiKey() {
    this.apiKey = null;
  }

  // 관리자 API 키 설정
  setAdminApiKey(adminApiKey: string) {
    this.adminApiKey = adminApiKey;
  }

  // 관리자 API 키 제거
  clearAdminApiKey() {
    this.adminApiKey = null;
  }

  // HTTP 요청 헬퍼
    private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseUrl}${endpoint}`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
      };

      // 관리자 API 경로인 경우 관리자 API 키 사용
      if (endpoint.startsWith('/api/admin/') && this.adminApiKey) {
        headers['Authorization'] = `Bearer ${this.adminApiKey}`;
      }
      // 일반 API인 경우 일반 API 키 사용
      else if (this.apiKey) {
        headers['Authorization'] = `Bearer ${this.apiKey}`;
      }

      const response = await fetch(url, {
        ...options,
        headers,
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API request error:', error);
      return {
        success: false,
        error: '네트워크 오류가 발생했습니다.'
      };
    }
  }

  // 사용자 등록
  async registerUser(data: RegisterRequest): Promise<ApiResponse> {
    return this.request('/api/users/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // 사용자 정보 조회
  async getUser(): Promise<ApiResponse> {
    return this.request('/api/users/me');
  }

  // 사용자 통계 조회
  async getUserStats(): Promise<ApiResponse> {
    return this.request('/api/users/stats');
  }

  // 통계 데이터 조회
  async getStats(): Promise<ApiResponse<StatsData>> {
    return this.request<StatsData>('/api/admin/stats');
  }

  // 최근 활동 조회
  async getRecentActivity(): Promise<ApiResponse<ActivityData[]>> {
    return this.request<ActivityData[]>('/api/admin/activity');
  }

  // 폼 생성
  async createForm(formData: FormData): Promise<ApiResponse<{ form_id: string }>> {
    return this.request<{ form_id: string }>('/api/admin/forms', {
      method: 'POST',
      body: JSON.stringify(formData),
    });
  }

  // 폼 목록 조회
  async getForms(): Promise<ApiResponse<{ forms: FormInfo[] }>> {
    return this.request<{ forms: FormInfo[] }>('/api/admin/forms');
  }

  // 미연결 폼 목록 조회
  async getUnconnectedForms(): Promise<ApiResponse<{ forms: FormInfo[] }>> {
    return this.request<{ forms: FormInfo[] }>('/api/admin/forms/unconnected');
  }

  // 폼에 이메일 연결
  async connectFormEmail(formId: string, email: string): Promise<ApiResponse> {
    return this.request(`/api/admin/forms/${formId}/connect`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  // 폼 상세 정보 조회
  async getFormDetail(formId: string): Promise<ApiResponse> {
    return this.request(`/api/admin/forms/${formId}`);
  }

  // 폼 이메일 및 허용 도메인 업데이트
  async updateFormEmail(formId: string, email?: string, allowedDomain?: string): Promise<ApiResponse> {
    const updateData: any = {};
    if (email !== undefined) updateData.email = email;
    if (allowedDomain !== undefined) updateData.allowed_domain = allowedDomain;
    
    return this.request(`/api/admin/forms/${formId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  // 제출 이력 조회
  async getSubmissions(formId?: string | null): Promise<ApiResponse<{ submissions: SubmissionInfo[], forms: FormInfo[] }>> {
    const endpoint = formId ? `/api/admin/submissions?form_id=${formId}` : '/api/admin/submissions';
    return this.request<{ submissions: SubmissionInfo[], forms: FormInfo[] }>(endpoint);
  }

  // 제출 상세 정보 조회
  async getSubmissionDetails(submissionId: string): Promise<ApiResponse<SubmissionInfo>> {
    return this.request<SubmissionInfo>(`/api/admin/submissions/${submissionId}`);
  }

  // 이메일 로그 조회
  async getEmailLogs(): Promise<ApiResponse<{ logs: EmailLogInfo[] }>> {
    return this.request<{ logs: EmailLogInfo[] }>('/api/admin/emails');
  }

  // 폼 삭제
  async deleteForm(formId: string): Promise<ApiResponse> {
    return this.request(`/api/admin/forms/${formId}`, {
      method: 'DELETE',
    });
  }

  // 폼 업데이트
  async updateForm(formId: string, updates: Partial<FormData>): Promise<ApiResponse> {
    return this.request(`/api/admin/forms/${formId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // 폼 기본 정보 업데이트 (이름, 메모)
  async updateFormInfo(formId: string, data: { name?: string; admin_notes?: string }): Promise<ApiResponse> {
    return this.request(`/api/admin/forms/${formId}/info`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // 관리자 설정 조회
  async getSettings(): Promise<ApiResponse<{ discord_webhook_url: string | null; discord_webhook_set: boolean }>> {
    return this.request('/api/admin/settings');
  }

  // 관리자 설정 저장
  async updateSettings(data: { discord_webhook_url?: string }): Promise<ApiResponse> {
    return this.request('/api/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // 폼별 Discord 웹훅 조회
  async getFormDiscordWebhook(formId: string): Promise<ApiResponse<{ discord_webhook_url: string | null; discord_webhook_set: boolean }>> {
    return this.request(`/api/admin/forms/${formId}/discord`);
  }

  // 폼별 Discord 웹훅 저장/삭제
  async updateFormDiscordWebhook(formId: string, webhookUrl: string | null): Promise<ApiResponse> {
    return this.request(`/api/admin/forms/${formId}/discord`, {
      method: 'PUT',
      body: JSON.stringify({ discord_webhook_url: webhookUrl || '' }),
    });
  }

  // 연결 상태 확인
  async checkConnection(): Promise<ApiResponse<{ status: string }>> {
    return this.request<{ status: string }>('/api/health');
  }

  // 건강성 체크
  async healthCheck(): Promise<ApiResponse> {
    return this.request('/health');
  }
} 